/**
 * Capa tipada de la COMPOSICIÓN de procesos (hito no-fundador · Lote 1).
 *
 * Métodos sobre {@link apiFetch} para el contrato del router
 * backend/app/api/v1/composicion.py: el facilitador arma el lienzo
 * proceso → fases → sesiones → actividades ANTES de correr la sala en vivo.
 *
 * Reglas del contrato que esta capa refleja en sus tipos y guardas:
 *   - POST /procesos lo puede hacer cualquier usuario de la org y siembra
 *     su Membresia como facilitador; toda otra mutación exige facilitador.
 *   - Las actividades solo se editan/mueven/quitan en estado 'preparada'
 *     (409 humano si ya corrieron); borrar fases/sesiones tiene guarda de
 *     cascada completa (409 si algo descendiente corrió o tiene aportes).
 *   - El árbol de GET /procesos/{id}/composicion es SERIALIZABLE: su forma
 *     es el futuro plantilla.manifest de M3-E2.
 *
 * Prefijo /api/v1 y Bearer los resuelve lib/api.ts.
 */
import { apiFetch } from './api';
import type { ActividadEstado, Herramienta } from './sesion';

/* ============================ Enums del lienzo ========================== */

/** Estados del proceso (enum EstadoProceso del backend). */
export type EstadoProceso = 'borrador' | 'activo' | 'cerrado' | 'archivado';

/** Tipo metodológico de una fase; 'neutra' es el default al crear. */
export type TipoFase = 'divergente' | 'convergente' | 'neutra';

/* ============================ Recursos planos =========================== */

/**
 * Catálogo de herramientas activas (GET /herramientas): la MISMA frontera C3
 * (manifiesto_publico) que ya consumen Bolsillo y Pantalla, así que se
 * reutiliza el tipo {@link Herramienta} de sesion.ts tal cual.
 */
export type HerramientaCatalogo = Herramienta;

export interface ProcesoComp {
  id: string;
  nombre: string;
  estado: EstadoProceso;
}

export interface FaseComp {
  id: string;
  orden: number;
  nombre: string;
  tipo: TipoFase;
}

export interface SesionComp {
  id: string;
  /** Fecha ISO (YYYY-MM-DD) o null si aún no se agenda. */
  fecha: string | null;
  /** Horas "HH:MM" o null. */
  hora_ini: string | null;
  hora_fin: string | null;
  estado: string;
}

export interface ActividadComp {
  id: string;
  sesion_id: string;
  orden: number;
  estado: ActividadEstado;
  duracion_min: number | null;
  modo: string | null;
  /** True si es diario de campo (asíncrona, M2). */
  asincrona: boolean;
  /**
   * Conexiones explícitas de inputs (Lote 4): {clave_input: actividad_id
   * fuente}. Vive en actividad.config.bindings (jsonb); {} si no hay ninguna.
   * Opcional por retro-compat con un backend anterior en la ventana de deploy.
   */
  bindings?: Record<string, string>;
  /** Vista compacta de la herramienta (misma forma que en el árbol). */
  herramienta: HerramientaArbol;
}

/* ============================ Árbol del lienzo ========================== */

/**
 * Resumen de herramienta que viaja DENTRO del árbol (forma compacta, no el
 * manifiesto completo): lo justo para dibujar la tarjeta de actividad.
 */
export interface HerramientaArbol {
  codigo: string;
  nombre: string;
  familia: string | null;
  duracion_min: number | null;
  modos: string[];
  /** Tipos de captura declarados por el manifiesto (p. ej. ['texto','voto']). */
  capturas_tipos: string[];
}

export interface ActividadArbol {
  id: string;
  orden: number;
  estado: ActividadEstado;
  duracion_min: number | null;
  modo: string | null;
  asincrona: boolean;
  /** Conexiones explícitas {clave_input: actividad_id fuente}; {} sin bindings. */
  bindings?: Record<string, string>;
  herramienta: HerramientaArbol;
}

export interface SesionArbol {
  id: string;
  fecha: string | null;
  hora_ini: string | null;
  hora_fin: string | null;
  estado: string;
  /** Σ duracion_min de sus actividades (la calcula el backend). */
  suma_min: number;
  actividades: ActividadArbol[];
}

export interface FaseArbol {
  id: string;
  orden: number;
  nombre: string;
  tipo: TipoFase;
  sesiones: SesionArbol[];
}

/**
 * Árbol único del lienzo (GET /procesos/{id}/composicion). Orden estable de
 * sesiones garantizado por el backend: fecha ASC nulls last, hora_ini ASC
 * nulls last, id ASC. Su forma serializable es el futuro plantilla.manifest.
 */
export interface ArbolComposicion {
  id: string;
  nombre: string;
  estado: EstadoProceso;
  problema_enunciado: string | null;
  fases: FaseArbol[];
}

/* ============================ Validador (Lote 3) ======================== */

/** Nivel de una advertencia del validador: naranja orienta, info sugiere. */
export type NivelAdvertencia = 'advertencia' | 'info';

/**
 * Una fila de GET /procesos/{id}/validacion. El backend ya las devuelve
 * ordenadas (advertencias primero, luego info; dentro, por posición en el
 * proceso) y con el texto interpolado de los datos reales — el panel solo
 * las pinta y resalta el destino (fase/sesión/actividad) al hacer clic.
 */
export interface Advertencia {
  regla: string;
  nivel: NivelAdvertencia;
  texto: string;
  fase_id: string | null;
  sesion_id: string | null;
  actividad_id: string | null;
}

export interface RespuestaValidacion {
  advertencias: Advertencia[];
}

/* ============================ Cuerpos de mutación ======================= */

export interface NuevoProceso {
  nombre: string;
  /** Problema de la org que origina el proceso (entrada por problema, P4). */
  problema_id?: string;
  /**
   * Slug de plantilla instanciable (Oleada 5): el proceso nace con fases,
   * sesiones, actividades y conexiones precableadas. Sin ella: lienzo vacío.
   */
  plantilla?: string;
}

export interface EdicionProceso {
  nombre?: string;
  estado?: EstadoProceso;
}

export interface NuevaFase {
  nombre: string;
  /** Default del backend: 'neutra'. */
  tipo?: TipoFase;
}

export interface EdicionFase {
  nombre?: string;
  tipo?: TipoFase;
  /** Reorden atómico en una transacción (UNIQUE proceso_id+orden). */
  orden?: number;
}

export interface NuevaSesion {
  fecha?: string;
  hora_ini?: string;
  hora_fin?: string;
}

export interface EdicionSesion {
  fecha?: string;
  hora_ini?: string;
  hora_fin?: string;
}

export interface NuevaActividad {
  herramienta_codigo: string;
  /** Si se omite, el backend toma el default del manifiesto. */
  duracion_min?: number;
  /** Si se omite, el backend toma manifest.modos[0]. */
  modo?: string;
}

export interface EdicionActividad {
  duracion_min?: number;
  modo?: string;
  /**
   * Conexiones de inputs (Lote 4): {clave_input: actividad_id fuente}. Un
   * valor null ELIMINA la clave (vuelve a la resolución automática por
   * cercanía). El backend valida clave, orden global y familia/captura (422).
   */
  bindings?: Record<string, string | null>;
}

export interface MoverActividad {
  /** Sesión destino (la misma u OTRA del mismo proceso). */
  sesion_id: string;
  /** Posición 1..n en la sesión destino. */
  orden: number;
}

/* ============================ Métodos API =============================== */

export const composicionApi = {
  /** Catálogo de herramientas activas (manifiesto público C3, mayor versión). */
  herramientas: () => apiFetch<HerramientaCatalogo[]>('/herramientas'),

  /** Crea un proceso en 'borrador' y te siembra como facilitador (cualquier usuario de la org). */
  crearProceso: (cuerpo: NuevoProceso) =>
    apiFetch<ProcesoComp>('/procesos', { method: 'POST', json: cuerpo }),

  /** Facilitador: renombra o cambia estado; activar NUNCA se bloquea por advertencias. */
  editarProceso: (id: string, cuerpo: EdicionProceso) =>
    apiFetch<ProcesoComp>(`/procesos/${id}`, { method: 'PATCH', json: cuerpo }),

  /** Facilitador: agrega una fase al final (orden = max+1, tipo default 'neutra'). */
  crearFase: (procesoId: string, cuerpo: NuevaFase) =>
    apiFetch<FaseComp>(`/procesos/${procesoId}/fases`, { method: 'POST', json: cuerpo }),

  /** Facilitador: edita nombre/tipo o reordena (reorden atómico en una transacción). */
  editarFase: (id: string, cuerpo: EdicionFase) =>
    apiFetch<FaseComp>(`/fases/${id}`, { method: 'PATCH', json: cuerpo }),

  /** Facilitador: 409 si CUALQUIER actividad descendiente corrió o tiene aportes/artefactos. */
  borrarFase: (id: string) => apiFetch<void>(`/fases/${id}`, { method: 'DELETE' }),

  /** Facilitador: agrega una sesión a la fase (fecha/horas opcionales). */
  crearSesion: (faseId: string, cuerpo: NuevaSesion = {}) =>
    apiFetch<SesionComp>(`/fases/${faseId}/sesiones`, { method: 'POST', json: cuerpo }),

  /** Facilitador: reagenda fecha/horas de la sesión. */
  editarSesion: (id: string, cuerpo: EdicionSesion) =>
    apiFetch<SesionComp>(`/sesiones/${id}`, { method: 'PATCH', json: cuerpo }),

  /** Facilitador: misma guarda de cascada que borrar fase (409 si algo corrió). */
  borrarSesion: (id: string) => apiFetch<void>(`/sesiones/${id}`, { method: 'DELETE' }),

  /** Facilitador: agrega actividad por código de herramienta (404 humano si no existe); defaults del manifiesto. */
  agregarActividad: (sesionId: string, cuerpo: NuevaActividad) =>
    apiFetch<ActividadComp>(`/sesiones/${sesionId}/actividades`, {
      method: 'POST',
      json: cuerpo,
    }),

  /** Facilitador: edita duración/modo SOLO en 'preparada' (409 sin carrera con la sala en vivo). */
  editarActividad: (id: string, cuerpo: EdicionActividad) =>
    apiFetch<ActividadComp>(`/actividades/${id}/composicion`, {
      method: 'PATCH',
      json: cuerpo,
    }),

  /** Facilitador: mueve y renumera origen y destino en UNA transacción; 409 si la actividad ya corrió. */
  moverActividad: (id: string, cuerpo: MoverActividad) =>
    apiFetch<ActividadComp>(`/actividades/${id}/mover`, { method: 'POST', json: cuerpo }),

  /** Facilitador: borra SOLO si está 'preparada' y sin aportes ni artefactos (409 si no). */
  quitarActividad: (id: string) =>
    apiFetch<void>(`/actividades/${id}`, { method: 'DELETE' }),

  /** Árbol único del lienzo (fases → sesiones → actividades, con suma_min). */
  arbol: (procesoId: string) =>
    apiFetch<ArbolComposicion>(`/procesos/${procesoId}/composicion`),

  /** Validador bajo demanda (advertencias orientan, no bloquean; sin caché servidor). */
  validacion: (procesoId: string) =>
    apiFetch<RespuestaValidacion>(`/procesos/${procesoId}/validacion`),
};

/* ============================ Query keys ================================ */

/** Llaves estables de TanStack Query para invalidar el lienzo con precisión. */
export const composicionKeys = {
  herramientas: ['herramientas'] as const,
  arbol: (procesoId: string) => ['composicion', procesoId] as const,
  /**
   * ANIDADA bajo la llave del árbol A PROPÓSITO: invalidateQueries matchea
   * por prefijo, así que el `invalidar` que ya disparan las mutaciones del
   * compositor sobre el árbol refresca TAMBIÉN la validación, sin agregar
   * una segunda invalidación en cada mutación.
   */
  validacion: (procesoId: string) => ['composicion', procesoId, 'validacion'] as const,
};
